import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import { WEB_STYLES } from '@/utils/webStyles';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export interface GlassSidePanelProps {
  visible: boolean;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
  showOverlay?: boolean;
  closeOnOverlayPress?: boolean;
  /** Side from which the panel slides in. Defaults to 'right' (matches DesktopSidePanel). */
  side?: 'left' | 'right';
  /** Glass strength. 'strong' uses the higher-opacity card token for legibility when stacked over other glass surfaces. */
  variant?: 'soft' | 'strong';
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
}

/**
 * Theme-aware side panel. Mirrors DesktopSidePanel's slide animation + ESC/overlay
 * dismiss but renders the panel surface as a frosted glass card driven by admin tokens.
 * Phase 3.4 wraps DesktopSidePanel in this primitive — for now it's a standalone shell.
 */
export const GlassSidePanel: React.FC<GlassSidePanelProps> = ({
  visible,
  onClose,
  width = 480,
  children,
  showOverlay = true,
  closeOnOverlayPress = true,
  side = 'right',
  variant = 'soft',
  style,
  contentStyle,
}) => {
  const styles = useStyles();
  const { colors, components } = useThemeColors();
  const panelBackground =
    variant === 'strong' ? colors.card.bgStrong : components.admin.dataTable.bg;
  const offscreen = side === 'right' ? width : -width;
  const progress = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) setMounted(true);
    const animation = Animated.timing(progress, {
      toValue: visible ? 1 : 0,
      duration: 200,
      easing: visible ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic),
      useNativeDriver: true,
    });
    animation.start(({ finished }) => {
      if (finished && !visible) setMounted(false);
    });
    return () => animation.stop();
  }, [visible, progress]);

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [offscreen, 0],
  });
  const overlayOpacity = progress;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const w: any = (globalThis as any)?.window;
    if (!w) return;
    const handler = (e: any) => {
      if (e.key === 'Escape' && visible) onClose();
    };
    w.addEventListener('keydown', handler);
    return () => w.removeEventListener('keydown', handler);
  }, [visible, onClose]);

  return (
    <Modal visible={mounted} transparent animationType="none" onRequestClose={onClose}>
      <View style={baseStyles.container}>
        {showOverlay && (
          <AnimatedPressable
            style={[styles.overlay, { opacity: overlayOpacity }]}
            onPress={closeOnOverlayPress ? onClose : undefined}
            pointerEvents={visible ? 'auto' : 'none'}
          />
        )}
        <Animated.View
          style={[
            baseStyles.panel,
            side === 'right' ? baseStyles.panelRight : baseStyles.panelLeft,
            styles.panel,
            { width, backgroundColor: panelBackground },
            { transform: [{ translateX }] },
            style,
          ]}
        >
          <View style={[styles.content, contentStyle]}>{children}</View>
        </Animated.View>
      </View>
    </Modal>
  );
};

GlassSidePanel.displayName = 'GlassSidePanel';

const baseStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  panel: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    overflow: 'visible',
  },
  panelLeft: { left: 0 },
  panelRight: { right: 0 },
});

const useStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.45)',
      },
      panel: {
        borderLeftWidth: 1,
        borderLeftColor: components.admin.dataTable.border,
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 12,
        shadowOffset: { width: -4, height: 0 },
        elevation: 12,
        ...(WEB_STYLES.backdropBlur(20) as object),
      },
      content: {
        flex: 1,
        backgroundColor: 'transparent',
      } as ViewStyle & { color?: string },
    }),
  );
